import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const BG = "https://images.unsplash.com/photo-1628712231228-f7e792e86880?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxiZWFyZGVkJTIwZHJhZ29uJTIwcmVwdGlsZSUyMGRhcmt8ZW58MHx8fHwxNzg1NTU0MTc5fDA&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : from);
    } catch (e) {
      setErr(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="App bg-[#080808] min-h-screen grid md:grid-cols-2">
      <div className="relative hidden md:block overflow-hidden">
        <img src={BG} alt="reptile" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/40 to-[#080808]" />
        <div className="absolute bottom-12 left-12 z-10">
          <p className="overline mb-3">Primal Feeders</p>
          <p className="font-display text-5xl font-light leading-tight max-w-sm">Fuel the predators<br />in your care.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-sm">
          <p className="overline mb-3">{mode === "login" ? "Welcome back" : "Join the colony"}</p>
          <h1 className="font-display font-light text-5xl tracking-tighter mb-10">{mode === "login" ? "Sign in." : "Create account."}</h1>

          <form onSubmit={submit} className="space-y-7">
            {mode === "register" && (
              <div>
                <label className="overline">Name</label>
                <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" />
              </div>
            )}
            <div>
              <label className="overline">Email</label>
              <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" />
            </div>
            <div>
              <label className="overline">Password</label>
              <input data-testid="auth-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field" />
            </div>
            {err && <p data-testid="auth-error" className="text-[#D94026] text-xs">{err}</p>}
            <button data-testid="auth-submit" disabled={loading} className="btn-toxic w-full py-4 disabled:opacity-50">
              {loading ? "…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <button data-testid="auth-toggle" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }} className="mt-8 text-sm text-[#8A8782] hover:text-[#C7F000] transition-colors">
            {mode === "login" ? "No account? Register →" : "Have an account? Sign in →"}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
